import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  Search,
  LayoutDashboard,
  Shield,
  ShieldCheck,
  Lock,
  ClipboardCheck,
  Target,
  BarChart3,
  Database,
  Heart,
  RefreshCw,
  Briefcase,
  FolderKanban,
  Bot,
  Settings,
  AlertTriangle,
  FileText,
  PlusCircle,
  FileBarChart,
  ArrowRight,
  Clock,
  X,
  Command,
  Wand2,
  Pin,
  Star,
  Palette,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import apiClient from '@/lib/api/client'
import { copilotApi } from '@/services/copilot.service'
import { openAndFillForm } from '@/lib/copilot/agent-runner'
import { resolveCreateEntry } from '@/lib/copilot/create-registry'
import { themeList, THEME_STORAGE_KEY, type ThemeName } from '@/lib/themes'

// ---------------------------------------------------------------------------
// Theme quick-apply helpers (command palette).
//
// The ThemeProvider / ColorThemeProvider are the source of truth via React
// state, but they read localStorage on mount and mirror to the DOM. To make
// theme changes from the palette instant AND consistent, we write the same
// localStorage keys the providers read and apply the DOM effect immediately.
// The providers reconcile on their next render (they also listen to storage).
// ---------------------------------------------------------------------------

const MODE_STORAGE_KEY = 'sgrc-theme'

function applyMode(mode: 'light' | 'dark' | 'system') {
  try {
    localStorage.setItem(MODE_STORAGE_KEY, mode)
  } catch {
    /* ignore */
  }
  const isDark =
    mode === 'dark' ||
    (mode === 'system' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches)
  const root = document.documentElement
  root.classList.toggle('dark', isDark)
  root.classList.toggle('light', !isDark)
  // Notify same-tab listeners (providers reconcile on their next mount too).
  window.dispatchEvent(new StorageEvent('storage', { key: MODE_STORAGE_KEY, newValue: mode }))
}

function applyAccent(name: ThemeName) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, name)
  } catch {
    /* ignore */
  }
  document.documentElement.setAttribute('data-color-theme', name)
  window.dispatchEvent(new StorageEvent('storage', { key: THEME_STORAGE_KEY, newValue: name }))
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CommandItem {
  id: string
  label: string
  description?: string
  icon: React.ReactNode
  category: 'Pages' | 'Actions' | 'Entites' | 'Theme'
  action: () => void
  keywords?: string[]
}

interface SearchResult {
  id: string
  title: string
  type: string
  url?: string
  description?: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RECENT_SEARCHES_KEY = 'sgrc-command-palette-recent'
const MAX_RECENT = 5
const FAVORITES_KEY = 'sgrc-command-palette-favorites'

// ---------------------------------------------------------------------------
// Hook: useCommandPalette
// ---------------------------------------------------------------------------

const CommandPaletteContext = React.createContext<{
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}>({ open: false, setOpen: () => {} })

export function useCommandPalette() {
  return React.useContext(CommandPaletteContext)
}

// ---------------------------------------------------------------------------
// Provider (wraps the palette + context)
// ---------------------------------------------------------------------------

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false)

  return (
    <CommandPaletteContext.Provider value={{ open, setOpen }}>
      {children}
      <CommandPalette open={open} onOpenChange={setOpen} />
    </CommandPaletteContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Navigation items
// ---------------------------------------------------------------------------

const ICON_CLS = 'h-4 w-4 shrink-0 text-muted-foreground'

function buildNavItems(navigate: (path: string) => void): CommandItem[] {
  return [
    { id: 'nav-dashboard', label: 'Dashboard', icon: <LayoutDashboard className={ICON_CLS} />, category: 'Pages', action: () => navigate('/dashboard'), keywords: ['accueil', 'home', 'tableau de bord'] },
    { id: 'nav-risks', label: 'Risques', icon: <AlertTriangle className={ICON_CLS} />, category: 'Pages', action: () => navigate('/risks'), keywords: ['risk', 'heatmap'] },
    { id: 'nav-governance-pro', label: 'GovernancePRO', icon: <Shield className={ICON_CLS} />, category: 'Pages', action: () => navigate('/governance-pro'), keywords: ['gouvernance', 'policies', 'politiques'] },
    { id: 'nav-risk-pro', label: 'RiskPRO', icon: <AlertTriangle className={ICON_CLS} />, category: 'Pages', action: () => navigate('/risk-pro'), keywords: ['risque', 'registre'] },
    { id: 'nav-security-pro', label: 'SecurityPRO', icon: <Lock className={ICON_CLS} />, category: 'Pages', action: () => navigate('/security-pro'), keywords: ['securite', 'iso 27001', 'soc2'] },
    { id: 'nav-compliance-pro', label: 'CompliancePRO', icon: <ShieldCheck className={ICON_CLS} />, category: 'Pages', action: () => navigate('/compliance-pro'), keywords: ['conformite', 'rgpd', 'loi 25'] },
    { id: 'nav-audit-pro', label: 'AuditPRO', icon: <ClipboardCheck className={ICON_CLS} />, category: 'Pages', action: () => navigate('/audit-pro'), keywords: ['audit', 'mission', 'findings'] },
    { id: 'nav-strategy-pro', label: 'StrategyPRO', icon: <Target className={ICON_CLS} />, category: 'Pages', action: () => navigate('/strategy-pro'), keywords: ['strategie', 'okr', 'bsc'] },
    { id: 'nav-maturity-pro', label: 'MaturityPRO', icon: <BarChart3 className={ICON_CLS} />, category: 'Pages', action: () => navigate('/maturity-pro'), keywords: ['maturite', 'evaluation', 'cmmi'] },
    { id: 'nav-data-pro', label: 'DataPRO', icon: <Database className={ICON_CLS} />, category: 'Pages', action: () => navigate('/data-pro'), keywords: ['donnees', 'data', 'qualite'] },
    { id: 'nav-ethics-pro', label: 'EthicsPRO', icon: <Heart className={ICON_CLS} />, category: 'Pages', action: () => navigate('/ethics-pro'), keywords: ['ethique', 'anti-corruption'] },
    { id: 'nav-change-pro', label: 'ChangePRO', icon: <RefreshCw className={ICON_CLS} />, category: 'Pages', action: () => navigate('/change-pro'), keywords: ['changement', 'adkar', 'kotter'] },
    { id: 'nav-bm-pro', label: 'BusinessModelPRO', icon: <Briefcase className={ICON_CLS} />, category: 'Pages', action: () => navigate('/business-model-pro'), keywords: ['business model', 'canvas', 'lean'] },
    { id: 'nav-workspace-pro', label: 'WorkspacePRO', icon: <FolderKanban className={ICON_CLS} />, category: 'Pages', action: () => navigate('/workspace-pro'), keywords: ['workspace', 'espace'] },
    { id: 'nav-ai-ia-pro', label: 'AI-IAGPRO', icon: <Bot className={ICON_CLS} />, category: 'Pages', action: () => navigate('/ai-ia-pro'), keywords: ['ia', 'intelligence artificielle', 'mlops', 'ai governance', 'iso 42001'] },
    { id: 'nav-vendor-pro', label: 'VendorPRO', icon: <Briefcase className={ICON_CLS} />, category: 'Pages', action: () => navigate('/vendor-pro'), keywords: ['tprm', 'fournisseurs', 'tiers', 'due diligence'] },
    { id: 'nav-dashboard-pro', label: 'DashboardPRO', icon: <BarChart3 className={ICON_CLS} />, category: 'Pages', action: () => navigate('/dashboard-pro'), keywords: ['tableaux de bord', 'kpi', 'heatmap', 'benchmark'] },
    { id: 'nav-hub', label: 'Hub', icon: <FolderKanban className={ICON_CLS} />, category: 'Pages', action: () => navigate('/hub'), keywords: ['hub', 'accueil contexte', 'fonctionnalites'] },
    { id: 'nav-reports', label: 'Rapports', icon: <FileBarChart className={ICON_CLS} />, category: 'Pages', action: () => navigate('/reports'), keywords: ['rapports', 'reporting', 'export'] },
    { id: 'nav-chat', label: 'AI Chat', icon: <Bot className={ICON_CLS} />, category: 'Pages', action: () => navigate('/chat'), keywords: ['ia', 'assistant', 'rag', 'intelligence'] },
    { id: 'nav-settings', label: 'Paramètres', icon: <Settings className={ICON_CLS} />, category: 'Pages', action: () => navigate('/settings'), keywords: ['parametres', 'configuration', 'reglages'] },
  ]
}

function buildActionItems(navigate: (path: string) => void): CommandItem[] {
  return [
    { id: 'act-risk', label: 'Creer un risque', icon: <PlusCircle className={ICON_CLS} />, category: 'Actions', action: () => navigate('/risk-pro/identify'), keywords: ['nouveau risque', 'ajouter risque'] },
    { id: 'act-policy', label: 'Nouvelle politique', icon: <FileText className={ICON_CLS} />, category: 'Actions', action: () => navigate('/governance-pro/policies'), keywords: ['policy', 'politique', 'creer politique'] },
    { id: 'act-audit', label: 'Nouvelle mission audit', icon: <ClipboardCheck className={ICON_CLS} />, category: 'Actions', action: () => navigate('/audit-pro/missions'), keywords: ['mission', 'creer audit'] },
    { id: 'act-report', label: 'Generer un rapport', icon: <FileBarChart className={ICON_CLS} />, category: 'Actions', action: () => navigate('/reports'), keywords: ['rapport', 'report', 'export'] },
    { id: 'act-eval', label: 'Nouvelle evaluation', icon: <BarChart3 className={ICON_CLS} />, category: 'Actions', action: () => navigate('/maturity-pro/campaigns'), keywords: ['evaluation', 'campagne', 'assessment'] },
    { id: 'act-control', label: 'Creer un controle', icon: <ShieldCheck className={ICON_CLS} />, category: 'Actions', action: () => navigate('/risk-pro/controls'), keywords: ['control', 'controle', 'nouveau controle'] },
  ]
}

function buildThemeItems(navigate: (path: string) => void): CommandItem[] {
  const modeItems: CommandItem[] = [
    { id: 'theme-mode-light', label: 'Thème : Clair', icon: <Sun className={ICON_CLS} />, category: 'Theme', action: () => applyMode('light'), keywords: ['light', 'clair', 'mode clair', 'theme'] },
    { id: 'theme-mode-dark', label: 'Thème : Sombre', icon: <Moon className={ICON_CLS} />, category: 'Theme', action: () => applyMode('dark'), keywords: ['dark', 'sombre', 'mode sombre', 'theme', 'nuit'] },
    { id: 'theme-mode-system', label: 'Thème : Auto (système)', icon: <Monitor className={ICON_CLS} />, category: 'Theme', action: () => applyMode('system'), keywords: ['system', 'auto', 'systeme', 'automatique', 'theme'] },
  ]
  const accentItems: CommandItem[] = themeList.map((th) => ({
    id: `theme-accent-${th.name}`,
    label: `Couleur : ${th.label}`,
    description: 'Accent',
    icon: (
      <span
        className="h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-black/10"
        style={{ background: `linear-gradient(135deg, hsl(${th.topbar}) 0%, hsl(${th.swatch}) 100%)` }}
      />
    ),
    category: 'Theme',
    action: () => applyAccent(th.name),
    keywords: ['couleur', 'accent', 'color', 'theme', th.name, th.label],
  }))
  const openSettings: CommandItem = {
    id: 'theme-open-settings',
    label: 'Ouvrir les paramètres d’apparence',
    icon: <Palette className={ICON_CLS} />,
    category: 'Theme',
    action: () => navigate('/settings'),
    keywords: ['apparence', 'theme', 'appearance', 'parametres', 'couleur', 'mode'],
  }
  return [...modeItems, ...accentItems, openSettings]
}

// ---------------------------------------------------------------------------
// Fuzzy match helper
// ---------------------------------------------------------------------------

/**
 * Normalize a string for accent-insensitive matching: strip diacritics
 * (NFD decomposition + combining-marks removal) and lower-case, so an
 * accented query like "conformité" matches an unaccented keyword like
 * "conformite" and vice-versa.
 */
function normalizeText(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

function fuzzyMatch(text: string, query: string): boolean {
  const lower = normalizeText(text)
  const q = normalizeText(query)
  let qi = 0
  for (let i = 0; i < lower.length && qi < q.length; i++) {
    if (lower[i] === q[qi]) qi++
  }
  return qi === q.length
}

function matchesItem(item: CommandItem, query: string): boolean {
  if (fuzzyMatch(item.label, query)) return true
  if (item.description && fuzzyMatch(item.description, query)) return true
  if (item.keywords?.some((kw) => fuzzyMatch(kw, query))) return true
  return false
}

// ---------------------------------------------------------------------------
// Recent searches helpers
// ---------------------------------------------------------------------------

function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function addRecentSearch(query: string) {
  if (!query.trim()) return
  const recent = getRecentSearches().filter((s) => s !== query)
  recent.unshift(query)
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)))
}

// ---------------------------------------------------------------------------
// Favorites (pinned pages/actions) helpers — persisted list of CommandItem ids
// ---------------------------------------------------------------------------

function getFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function saveFavorites(ids: string[]) {
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids))
  } catch {
    /* ignore quota / serialization errors */
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [query, setQuery] = React.useState('')
  const [activeIndex, setActiveIndex] = React.useState(0)
  const [apiResults, setApiResults] = React.useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = React.useState(false)
  const [recentSearches, setRecentSearches] = React.useState<string[]>([])
  const [favorites, setFavorites] = React.useState<string[]>([])

  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const navItems = React.useMemo(() => buildNavItems(navigate), [navigate])
  const actionItems = React.useMemo(() => buildActionItems(navigate), [navigate])
  const themeItems = React.useMemo(() => buildThemeItems(navigate), [navigate])
  const allStatic = React.useMemo(
    () => [...navItems, ...actionItems, ...themeItems],
    [navItems, actionItems, themeItems],
  )

  // ---- favorites (pinned pages/actions) ----
  const toggleFavorite = React.useCallback((id: string) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [id, ...prev]
      saveFavorites(next)
      return next
    })
  }, [])

  // Set of pinnable ids — only static pages/actions (not API entities or the
  // transient "Créer avec l'IA" item) can be pinned to favorites.
  const staticIdSet = React.useMemo(() => new Set(allStatic.map((s) => s.id)), [allStatic])

  // Resolve favorite ids back to their static CommandItem (skip stale ids).
  const favoriteItems = React.useMemo(
    () =>
      favorites
        .map((id) => allStatic.find((it) => it.id === id))
        .filter((it): it is CommandItem => Boolean(it)),
    [favorites, allStatic],
  )

  // ---- filtered static items ----
  const filteredStatic = React.useMemo(() => {
    // Empty query: show pages + actions, but hide the (numerous) Theme commands
    // so the default list stays focused. They surface as soon as the user types
    // ("dark", "sombre", "couleur", a theme name…).
    if (!query.trim()) return allStatic.filter((item) => item.category !== 'Theme')
    return allStatic.filter((item) => matchesItem(item, query))
  }, [query, allStatic])

  // ---- API entity results mapped to CommandItem ----
  const entityItems: CommandItem[] = React.useMemo(
    () =>
      apiResults.map((r) => ({
        id: `entity-${r.id}`,
        label: r.title,
        description: r.type,
        icon: <ArrowRight className={ICON_CLS} />,
        category: 'Entites' as const,
        action: () => {
          if (r.url) navigate(r.url)
        },
        keywords: [],
      })),
    [apiResults, navigate]
  )

  // ---- "Créer avec l'IA…" agentic smart-create item ----
  // Uses the typed query as the natural-language prompt; resolves the target
  // module's create form, opens + pre-fills it through the HITL diff.
  const aiCreateItem: CommandItem = React.useMemo(
    () => ({
      id: 'ai-create',
      label: query.trim() ? `Créer avec l'IA : « ${query.trim()} »` : 'Créer avec l’IA…',
      description: 'IA',
      icon: <Wand2 className="h-4 w-4 shrink-0 text-primary" />,
      category: 'Actions',
      action: () => {
        const prompt = query.trim()
        const module = resolveCreateEntry(prompt || 'risque', undefined).module
        void openAndFillForm(module, prompt)
      },
      keywords: ['creer avec ia', 'creer', 'nouveau', 'nouvelle', 'ia', 'ai create', 'generer'],
    }),
    [query],
  )

  // ---- grouped results ----
  const grouped = React.useMemo(() => {
    const pages = filteredStatic.filter((i) => i.category === 'Pages')
    const actions = filteredStatic.filter((i) => i.category === 'Actions')
    const themeCmds = filteredStatic.filter((i) => i.category === 'Theme')
    const entities = entityItems
    const groups: { label: string; items: CommandItem[] }[] = []
    if (pages.length) groups.push({ label: 'Pages', items: pages })
    if (actions.length) groups.push({ label: 'Actions', items: actions })
    if (themeCmds.length) groups.push({ label: 'Thème', items: themeCmds })
    if (entities.length) groups.push({ label: 'Entites', items: entities })
    // Always offer the agentic smart-create at the bottom (or as the sole option
    // when a natural-language "Créer …" prompt matches nothing else).
    groups.push({ label: "Créer avec l'IA", items: [aiCreateItem] })
    return groups
  }, [filteredStatic, entityItems, aiCreateItem])

  const flatItems = React.useMemo(() => grouped.flatMap((g) => g.items), [grouped])

  // ---- debounced API search ----
  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!query.trim() || query.length < 2) {
      setApiResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    debounceRef.current = setTimeout(async () => {
      try {
        // Semantic (cross-entity) search via knowledge-service.
        const hits = await copilotApi.semanticSearch(query, { limit: 10 })
        if (hits.length > 0) {
          setApiResults(
            hits.map((h) => ({
              id: h.entity_id,
              title: h.title,
              type: h.entity_type,
              url: h.url,
              description: h.snippet,
            })),
          )
        } else {
          // Fallback: legacy keyword entity search.
          const res = await apiClient.get<{ results?: SearchResult[] }>('/search', {
            params: { q: query, limit: 10 },
          })
          setApiResults(res.data?.results ?? [])
        }
      } catch {
        // Semantic errored — try the legacy keyword endpoint before giving up.
        try {
          const res = await apiClient.get<{ results?: SearchResult[] }>('/search', {
            params: { q: query, limit: 10 },
          })
          setApiResults(res.data?.results ?? [])
        } catch {
          setApiResults([])
        }
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query])

  // ---- reset on open/close ----
  React.useEffect(() => {
    if (open) {
      setQuery('')
      setActiveIndex(0)
      setApiResults([])
      setRecentSearches(getRecentSearches())
      setFavorites(getFavorites())
      // Focus input after dialog animation
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  // ---- clamp active index ----
  React.useEffect(() => {
    if (activeIndex >= flatItems.length) setActiveIndex(Math.max(0, flatItems.length - 1))
  }, [flatItems.length, activeIndex])

  // ---- global keyboard shortcut ----
  React.useEffect(() => {
    function handleGlobalKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    document.addEventListener('keydown', handleGlobalKey)
    return () => document.removeEventListener('keydown', handleGlobalKey)
  }, [open, onOpenChange])

  // ---- internal keyboard nav ----
  function handleKeyDown(e: React.KeyboardEvent) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((prev) => (prev + 1) % Math.max(flatItems.length, 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((prev) => (prev - 1 + flatItems.length) % Math.max(flatItems.length, 1))
        break
      case 'Enter':
        e.preventDefault()
        if (flatItems[activeIndex]) {
          selectItem(flatItems[activeIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        onOpenChange(false)
        break
    }
  }

  function selectItem(item: CommandItem) {
    addRecentSearch(query || item.label)
    item.action()
    onOpenChange(false)
  }

  function handleRecentClick(recent: string) {
    setQuery(recent)
    setActiveIndex(0)
  }

  // ---- scroll active item into view ----
  const listRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    const container = listRef.current
    if (!container) return
    const active = container.querySelector('[data-active="true"]')
    if (active) {
      active.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  // ---- render ----
  const showRecent = !query.trim() && recentSearches.length > 0
  const showFavorites = !query.trim() && favoriteItems.length > 0

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[20%] z-50 w-full max-w-xl translate-x-[-50%] rounded-xl border bg-popover shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=open]:slide-in-from-left-1/2"
          onKeyDown={handleKeyDown}
          aria-label="Command palette"
        >
          {/* Accessible but hidden title for screen readers */}
          <DialogPrimitive.Title className="sr-only">
            Command Palette
          </DialogPrimitive.Title>

          {/* Search input */}
          <div className="flex items-center gap-2 border-b px-4 py-3">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setActiveIndex(0)
              }}
              placeholder="Rechercher pages, actions, entites..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              autoComplete="off"
              spellCheck={false}
            />
            {isSearching && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
            )}
            <button
              onClick={() => onOpenChange(false)}
              className="rounded p-0.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Shortcut hint */}
          <div className="flex items-center justify-between border-b px-4 py-1.5 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-2">
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">&uarr;&darr;</kbd>
                naviguer
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">&crarr;</kbd>
                ouvrir
              </span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border bg-muted px-1 py-0.5 font-mono text-[10px]">?</kbd>
              tous les raccourcis
            </span>
          </div>

          {/* Results list */}
          <div ref={listRef} className="max-h-[360px] overflow-y-auto overscroll-contain py-1">
            {/* Favorites (pinned pages/actions) */}
            {showFavorites && (
              <div className="px-2 py-1.5">
                <p className="flex items-center gap-1 px-2 pb-1 text-xs font-medium text-muted-foreground">
                  <Star className="h-3 w-3 fill-current text-amber-500" />
                  Favoris
                </p>
                {favoriteItems.map((item) => (
                  <div key={item.id} className="group/fav relative">
                    <button
                      onClick={() => selectItem(item)}
                      className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 pr-8 text-sm text-foreground hover:bg-accent/50"
                    >
                      {item.icon}
                      <span className="flex-1 truncate text-left">{item.label}</span>
                      {item.description && (
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {item.description}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleFavorite(item.id)
                      }}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-amber-500 hover:text-foreground"
                      title="Retirer des favoris"
                      aria-label="Retirer des favoris"
                    >
                      <Star className="h-3.5 w-3.5 fill-current" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Recent searches */}
            {showRecent && (
              <div className="px-2 py-1.5">
                <p className="px-2 pb-1 text-xs font-medium text-muted-foreground">
                  Recherches recentes
                </p>
                {recentSearches.map((recent) => (
                  <button
                    key={recent}
                    onClick={() => handleRecentClick(recent)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-accent"
                  >
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                    {recent}
                  </button>
                ))}
              </div>
            )}

            {/* Grouped results */}
            {grouped.length === 0 && query.trim() && !isSearching && (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                Aucun resultat pour &laquo;&nbsp;{query}&nbsp;&raquo;
              </p>
            )}

            {grouped.map((group) => {
              // Compute the starting flat index for this group
              let startIdx = 0
              for (const g of grouped) {
                if (g === group) break
                startIdx += g.items.length
              }
              return (
                <div key={group.label} className="px-2 py-1.5">
                  <p className="px-2 pb-1 text-xs font-medium text-muted-foreground">
                    {group.label}
                  </p>
                  {group.items.map((item, i) => {
                    const flatIdx = startIdx + i
                    const isActive = flatIdx === activeIndex
                    // Only static pages/actions can be pinned (stable ids); API
                    // entities and the AI-create item are transient.
                    const pinnable = staticIdSet.has(item.id)
                    const isFav = pinnable && favorites.includes(item.id)
                    return (
                      <div key={item.id} className="group/item relative">
                        <button
                          data-active={isActive}
                          onClick={() => selectItem(item)}
                          onMouseEnter={() => setActiveIndex(flatIdx)}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors',
                            pinnable && 'pr-8',
                            isActive
                              ? 'bg-accent text-accent-foreground'
                              : 'text-foreground hover:bg-accent/50'
                          )}
                        >
                          {item.icon}
                          <span className="flex-1 truncate text-left">{item.label}</span>
                          {item.description && (
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {item.description}
                            </span>
                          )}
                          {isActive && !pinnable && (
                            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          )}
                        </button>
                        {pinnable && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleFavorite(item.id)
                            }}
                            className={cn(
                              'absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 transition-opacity',
                              isFav
                                ? 'text-amber-500 opacity-100'
                                : 'text-muted-foreground opacity-0 hover:text-foreground group-hover/item:opacity-100'
                            )}
                            title={isFav ? 'Retirer des favoris' : 'Épingler aux favoris'}
                            aria-label={isFav ? 'Retirer des favoris' : 'Épingler aux favoris'}
                          >
                            {isFav ? (
                              <Star className="h-3.5 w-3.5 fill-current" />
                            ) : (
                              <Pin className="h-3.5 w-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
